import axios from 'axios'
import { environment } from '../environments/environment'

const apiEndpoint = environment.webservices.endpoint

export interface JobMeta {
  task_id: string | null
  job_key: string
  display_name: string
  source: 'manual' | 'beat'
  status: 'IDLE' | 'QUEUED' | 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'REVOKED' | 'DISABLED'
  started_by: string
  queued_at: number | null
  started_at: number | null
  completed_at: number | null
  error: string | null
}

export interface ActiveJobs {
  running: JobMeta[]
  queued: JobMeta[]
  recent: JobMeta[]
  disabled?: boolean
}

const DISABLED_RESPONSE: ActiveJobs = { running: [], queued: [], recent: [], disabled: true }

export const backgroundJobService = {
  getActiveJobs(): Promise<ActiveJobs> {
    return axios.get(`${apiEndpoint}/jobs/active`).then((r) => r.data).catch((err) => {
      if (err?.response?.status === 404) return DISABLED_RESPONSE
      throw err
    })
  },

  cancel(jobKey: string): Promise<JobMeta> {
    return axios.post(`${apiEndpoint}/jobs/${jobKey}/cancel`).then((r) => r.data)
  },

  /**
   * Poll /jobs/active at a fixed interval.
   * Returns cleanup function to stop polling.
   */
  pollActive(
    onUpdate: (data: ActiveJobs) => void,
    intervalMs: number = 3000
  ): () => void {
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const poll = async () => {
      if (cancelled) return
      try {
        const data = await backgroundJobService.getActiveJobs()
        if (!cancelled) onUpdate(data)
      } catch {
        // Silently retry on network errors
      }
      if (!cancelled) {
        timeoutId = setTimeout(poll, intervalMs)
      }
    }

    poll()
    return () => {
      cancelled = true
      if (timeoutId !== null) clearTimeout(timeoutId)
    }
  },
}

export function formatTimeAgo(timestamp: number | null): string {
  if (timestamp === null || timestamp === undefined) return ''
  const seconds = Math.floor(Date.now() / 1000 - timestamp)
  if (seconds < 0) return 'just now'
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
